import React, { useState } from 'react'
import i18n from 'simple-react-i18n'
import { isUndefined, uniq, uniqBy } from 'lodash'
import PropTypes from 'prop-types'
import { shallowEqual, useSelector } from 'react-redux'
import SimpleFilterSelect from 'components/forms/specific/SimpleFilterSelect'
import { Button, Card, CardContent, Grid, Icon } from '@mui/material'
import SuperMultiAutocomplete from 'components/forms/SuperMultiAutocomplete'
import Select from 'components/forms/Select'
import { getTypeModule } from 'utils/StationUtils'
import Input from 'components/forms/Input'
import { hasValue } from 'utils/NumberUtil'
import FilterInsertModal from 'components/modal/FilterInsertModal'
import { DEFAULT_CONTRIBUTOR_TYPE, FILTER_TYPE } from 'administration/components/user/constants/UserConstants'
import MultiContributorsAutocomplete from 'referencial/components/contributor/components/MultiContributorsAutocomplete'
import useApplicationSetting, { intParser } from 'utils/customHook/useApplicationSetting'
import moment from 'moment'
import DtoContributorLink from 'station/dto/DtoContributorLink'
import SimpleCheckbox from 'components/forms/SimpleCheckbox'
import { CONFORMITY_COLORS } from 'quality/utils/conformityExportUtils'
import { INDICATORS_COLORS } from 'utils/constants/ColorTheme'
import ProgressCard from 'components/card/ProgressCard'
import { CONFORM, NO_DATA, NO_INDICATOR, NON_CONFORM, REFERENCE_EXCEEDEDED } from 'distributionUnit/utils/DistributionUnitUtils'
import useSandreList from 'utils/customHook/useSandreList'
import { SANDRE } from 'referencial/constants/ReferencialConstants'

const INDICATOR_CHECKBOXES = [
    { value: NON_CONFORM, i18nKey: 'nonConform', backgroundColor: CONFORMITY_COLORS.nonConform },
    { value: REFERENCE_EXCEEDEDED, i18nKey: 'referenceExceeded', backgroundColor: CONFORMITY_COLORS.referenceExceeded },
    { value: CONFORM, i18nKey: 'compliant', backgroundColor: CONFORMITY_COLORS.conform },
    { value: NO_DATA, i18nKey: 'noData', backgroundColor: INDICATORS_COLORS.GREY },
    { value: NO_INDICATOR, i18nKey: 'notDetermined', backgroundColor: INDICATORS_COLORS.BLACK },
]

const DistributionUnitFilterPanel = ({
    stationType,
    data = [],
    contributorLinks = [],
    defaultFilter = {},
    onValidate = () => {},
    complianceProgress = 100,
}) => {
    const {
        cities,
        distributionUnits,
        distributionUnitSectors,
        contributorsIndex,
    } = useSelector(store => ({
        cities: store.CityReducer.cities,
        distributionUnits: store.DistributionUnitReducer.distributionUnits,
        distributionUnitSectors: store.DistributionUnitReducer.distributionUnitSectors,
        contributorsIndex: store.ContributorReducer.contributorsIndex,
    }), shallowEqual)

    const [filters, setFilters] = useState(defaultFilter)
    const [reset, setReset] = useState(false)
    const [open, setOpen] = useState(false)

    const typeModule = getTypeModule(stationType)
    const contributorsList = useSandreList(SANDRE.CONTRIBUTOR_TYPES)
    const operatorType = useApplicationSetting('contributorTypeOperator', intParser) || DEFAULT_CONTRIBUTOR_TYPE.OPERATOR
    const referentType = useApplicationSetting('contributorTypeAdministrator', intParser) || DEFAULT_CONTRIBUTOR_TYPE.ADMINISTRATOR

    const now = moment().valueOf()
    const referentsList = uniq(contributorLinks.filter(c => c.contributorType === referentType && (c.endDate >= now || !c.endDate)).map(l => l.idContributor)).map(id => contributorsIndex[id]).filter(c => !isUndefined(c))
    const operatorsList = uniq(contributorLinks.filter(c => c.contributorType === operatorType && (c.endDate >= now || !c.endDate)).map(l => l.idContributor)).map(id => contributorsIndex[id]).filter(c => !isUndefined(c))

    const onCancel = () => {
        setReset(filters.filter !== FILTER_TYPE.NOFILTER)
        setFilters({
            searchValue: '',
            filter: FILTER_TYPE.NOFILTER,
            filterResults: [],
            sector: undefined,
            city: undefined,
            referentIds: [],
            operatorIds: [],
            indicators: [NO_INDICATOR, CONFORM, REFERENCE_EXCEEDEDED, NON_CONFORM, NO_DATA],
        })
    }

    const filteredTowns = distributionUnits?.flatMap(d => d.geographicSector?.split(','))

    return (
        <Card>
            <CardContent>
                <Grid container spacing={1.5}>
                    <Grid size={4}>
                        <Input
                            tooltip={i18n.searchThenEnter}
                            title={i18n.search}
                            value={filters.searchValue}
                            onChange={v => setFilters({ ...filters, searchValue: v })}
                            onEnterKeyPress={(value) => {
                                setFilters({ ...filters, searchValue: value })
                                onValidate({ ...filters, searchValue: value })
                            }}
                            clearFunction
                            data-cy='search'
                        />
                    </Grid>
                    <Grid size={3.5}>
                        <SimpleFilterSelect
                            stationType={stationType}
                            onChange={(filterStations, newFilter) => {
                                setReset(false)
                                setFilters({ ...filters, filter: newFilter, filterResults: filterStations })
                            }}
                            nullLabel={i18n.allStations}
                            stations={data}
                            value={reset ? FILTER_TYPE.NOFILTER : filters.filter}
                        />
                    </Grid>
                    <Grid container size={0.5} alignItems='center'>
                        <Icon sx={{ cursor: 'pointer' }} onClick={() => setOpen(true)} data-cy='open-filter' fontSize='md'>
                            {hasValue(filters.filter) && filters.filter !== FILTER_TYPE.BOOKMARK && filters.filter !== FILTER_TYPE.NOFILTER ? 'edit' : 'note_add'}
                        </Icon>
                    </Grid>
                    <FilterInsertModal
                        setOpen={setOpen}
                        open={open}
                        selectedFilter={filters.filter}
                        module={typeModule}
                        isNew={!hasValue(filters.filter) || filters.filter === FILTER_TYPE.BOOKMARK || filters.filter === FILTER_TYPE.NOFILTER}
                        onChange={v => setFilters({ ...filters, filter: v })}
                    />
                    <Grid size={4}>
                        <SuperMultiAutocomplete
                            options={uniqBy(distributionUnitSectors, 'name')}
                            label={i18n.sector}
                            onChange={v => setFilters({ ...filters, sector: v })}
                            values={filters.sector}
                        />
                    </Grid>
                    <Grid size={4}>
                        <Select
                            options={cities.filter(c => filteredTowns.includes(c.code))}
                            label={i18n.city}
                            onChange={v => setFilters({ ...filters, city: v })}
                            value={filters.city}
                            clearFunction
                            noNullValue
                        />
                    </Grid>
                    {!!referentsList.length && (
                        <Grid size={4}>
                            <MultiContributorsAutocomplete
                                label={contributorsList.find(c => c.code === referentType)?.name}
                                options={referentsList}
                                values={filters.referentIds}
                                onChange={v => setFilters({ ...filters, referentIds: v })}
                                keyValue='code'
                                multiple
                            />
                        </Grid>
                    )}
                    {!!operatorsList.length && (
                        <Grid size={4}>
                            <MultiContributorsAutocomplete
                                label={contributorsList.find(c => c.code === operatorType)?.name}
                                options={operatorsList}
                                values={filters.operatorIds}
                                onChange={v => setFilters({ ...filters, operatorIds: v })}
                                keyValue='code'
                                multiple
                            />
                        </Grid>
                    )}
                    <Grid container size={12} sx={{ alignItems: 'center' }}>
                        {complianceProgress === 100 && INDICATOR_CHECKBOXES.map(({ value, i18nKey, backgroundColor }, index) => (
                            <Grid size={2.4} key={value} sx={index ? { paddingLeft: '1rem' } : {}}>
                                <SimpleCheckbox
                                    label={i18n[i18nKey]}
                                    checked={filters.indicators.includes(value)}
                                    onToggle={() => setFilters(prev => ({ ...prev, indicators: prev.indicators.includes(value) ? prev.indicators.filter(p => p !== value) : [value, ...prev.indicators] }))}
                                    labelStyle={{ padding: '0.25rem 1rem', borderRadius: '5px', backgroundColor, color: 'white !important', minHeight: window.innerWidth < 1250 ? '3rem' : '0px', textAlign: 'center' }}
                                />
                            </Grid>
                        )) || (
                            complianceProgress !== -1 && (
                                <Grid size={12}>
                                    <ProgressCard
                                        progress={complianceProgress}
                                        message={i18n.loadingCompliance}
                                    />
                                </Grid>
                            )
                        )}
                    </Grid>
                    <Grid container size={12} justifyContent='flex-end'>
                        <Grid offset={'auto'} size='auto' >
                            <Button
                                variant='outlined'
                                fullWidth
                                onClick={onCancel}
                            >
                                { i18n.reinit }
                            </Button>
                        </Grid>
                        <Grid size='auto'>
                            <Button
                                variant='contained'
                                onClick={() => onValidate(filters)}
                                data-cy='searchBtn'
                            >
                                {i18n.search}
                            </Button>
                        </Grid>
                    </Grid>
                </Grid>
            </CardContent>
        </Card>
    )
}

DistributionUnitFilterPanel.propTypes = {
    stationType: PropTypes.string,
    data: PropTypes.arrayOf(PropTypes.shape({})),
    contributorLinks: PropTypes.arrayOf(PropTypes.instanceOf(DtoContributorLink)),
    defaultFilter: PropTypes.shape({}),
    onValidate: PropTypes.func,
    complianceProgress: PropTypes.number,
}


export default DistributionUnitFilterPanel